import React from 'react';
import './ui.css';

/**
 * A row of mutually exclusive options, all visible at once.
 *
 * Replaces a dropdown wherever there are four or fewer choices — format,
 * match type, tournament, draw method, scoring. A select hides both the
 * options and the current value behind a click; at these counts there is no
 * reason to make anyone open anything.
 *
 * Built on real radio inputs rather than buttons with aria-checked. That
 * gives arrow-key navigation, the roving tab stop and screen-reader
 * grouping without writing any of it, and it keeps working if the styles
 * fail to load. The inputs are hidden from sight, not from assistive tech.
 *
 * Options may be given as strings, or as { value, label, disabled }.
 */
function Segmented({ ariaLabel, disabled = false, name, onChange, options = [], style, value }) {
  // Radios only behave as one group if they share a name, and two Segmenteds
  // on the same screen must not share one. useId gives a stable per-instance
  // fallback so callers only pass `name` when they want a specific one.
  const generatedName = React.useId();
  const groupName = name || generatedName;

  const items = options.map(opt => (
    typeof opt === 'object' && opt !== null ? opt : { value: opt, label: String(opt) }
  ));

  return (
    <div
      className={`gs-segmented${disabled ? ' gs-segmented-disabled' : ''}`}
      role="radiogroup"
      aria-label={ariaLabel}
      style={style}>
      {items.map(item => {
        const on = String(item.value) === String(value);
        const off = disabled || item.disabled;
        return (
          <label
            key={item.value}
            className={`gs-segmented-option${on ? ' gs-segmented-option-on' : ''}`}
            title={item.title || item.label}>
            <input
              type="radio"
              name={groupName}
              value={item.value}
              checked={on}
              disabled={off}
              // Guarded as well as disabled: the input is wrapped in a label,
              // and a click landing on the label rather than the input is a
              // second path to the same handler.
              onChange={() => { if (!off && onChange) onChange(item.value); }} />
            <span>{item.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default Segmented;
